// El autocompletado del widget: lo que en la home hacen typeahead.bundle 0.11.0 y Bloodhound.
//
// Se reescribe en vez de llevarse la librería por lo de siempre —el widget no puede arrastrar
// jQuery a la landing de un tercero— y por una razón concreta de este proyecto: typeahead
// envuelve el campo en un `<span class="twitter-typeahead">` con ese nombre de clase, y en el
// widget de Ofertren una landing que tenía su propio `.twitter-typeahead { display: none }`
// llegó a ocultar los campos de estación enteros.
//
// Lo que hay que respetar del comportamiento actual:
//   - `minLength: 0`: al enfocar, sin escribir nada, ya se pide la lista;
//   - lo que se guarda NO es el texto sino la fila entera que devolvió el servidor, porque en
//     varias ramas de salida viaja serializada entera en la URL (_paramOriginObject);
//   - teclear invalida la selección anterior (en la home, cada keydown borra typeaheadValue);
//   - al elegir, el campo pierde el foco.
//
// Lo que se añade, porque typeahead no lo daba: el combobox se anuncia de verdad
// (role="combobox", aria-expanded, aria-activedescendant) y se maneja con el teclado.

const ESPERA_MS = 250        // lo que se espera a que deje de teclear antes de preguntar
// Cuántas letras hacen falta antes de ofrecer nada. Los campos de estación piden la lista con
// el campo vacío (`minLength: 0` en typeahead); el de ocio no ofrece nada hasta la primera
// letra, porque su catálogo trae cientos de filas y enseñarlas todas no ayuda a nadie.
const MIN_LETRAS = 0

// El alto de la lista, para que quepa en lo que se ve. El máximo es el mismo `max-height: 320px`
// del CSS; el mínimo, lo que ocupan dos filas y pico: por debajo de ahí la lista estorba más de
// lo que ayuda, y siempre es mejor que asome por debajo del teclado a que no se vea nada.
const ALTO_MAXIMO_LISTA = 320
const ALTO_MINIMO_LISTA = 140
const MARGEN_LISTA = 8

/**
 * Quita acentos y pasa a minúsculas, para comparar lo que escribe el cliente con lo que
 * devuelve el servidor. "Málaga" tiene que encontrarse escribiendo "malaga".
 */
export function normalizar (texto) {
  return String(texto == null ? '' : texto)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().trim()
}

let contador = 0

/**
 * Cablea un campo de texto con su lista de sugerencias.
 *
 * @param {object} opciones
 * @param {HTMLInputElement} opciones.campo
 * @param {Element} opciones.overlays         dónde colgar la lista (fuera del widget)
 * @param {(q: string, señal: AbortSignal) => Promise<Array>} opciones.buscar
 * @param {(item: any) => string} [opciones.etiqueta]   el texto que se ve y se escribe en el campo
 * @param {(item: any) => string} [opciones.pintar]     el HTML de cada fila (ya escapado)
 * @param {(item: any) => void} opciones.alElegir
 * @param {() => void} [opciones.alInvalidar]  cuando el cliente teclea y la elección deja de valer
 * @param {string} [opciones.clases]     clases sueltas para la lista, cuando el desplegable de
 *                                       ese campo no se ve como el de las estaciones
 * @param {Element} [opciones.anclaje]   a quién se alinea la lista; por defecto, el campo
 * @param {(q: string) => Array|null} [opciones.adelanto]  lo que se puede enseñar YA, sin
 *        esperar al servidor; se pinta al instante y la respuesta de `buscar` lo sustituye
 * @param {(q: string, filas: Array) => void} [opciones.alResponder]  lo que ha contestado el
 *        servidor, para quien quiera quedárselo
 * @returns {{destruir: Function, cerrar: Function, fijar: Function}}
 */
export function autocompletar (opciones) {
  const {
    campo, overlays, buscar,
    etiqueta = (item) => (item && item.nombre) || '',
    pintar = null,
    alElegir,
    alInvalidar = () => {},
    minimo = MIN_LETRAS,
    clases = '',
    anclaje = null,
    adelanto = null,
    alResponder = null,
  } = opciones

  const id = 'rvb-ac' + (++contador)
  const lista = document.createElement('ul')
  lista.className = 'rvb-tt-menu rvb-oculto' + (clases ? ' ' + clases : '')
  lista.id = id
  lista.setAttribute('role', 'listbox')
  overlays.appendChild(lista)

  campo.setAttribute('role', 'combobox')
  campo.setAttribute('aria-autocomplete', 'list')
  campo.setAttribute('aria-expanded', 'false')
  campo.setAttribute('aria-controls', id)
  campo.setAttribute('autocomplete', 'off')

  let resultados = []
  let marcado = -1        // la fila resaltada con el teclado
  let temporizador = null
  let peticion = null     // AbortController de la petición en vuelo
  let abierta = false

  function colocar () {
    // La lista se cuelga al final de <body> (por el z-index), así que hay que llevarla a
    // mano hasta debajo del campo, y seguirla moviendo si la página hace scroll.
    // El desplegable de Ocio no arranca donde el campo, sino donde el recuadro entero: ahí
    // el rótulo ("Actividad") se queda dentro de la lista, como en la home.
    const r = (anclaje || campo).getBoundingClientRect()
    lista.style.position = 'absolute'
    lista.style.top = (r.bottom + window.scrollY) + 'px'
    lista.style.left = (r.left + window.scrollX) + 'px'
    lista.style.width = r.width + 'px'

    // Y QUE QUEPA EN LO QUE SE VE. El alto de la lista lo pone el CSS (`max-height: 320px`),
    // que es una medida de escritorio: en un móvil con el teclado fuera, el hueco visible por
    // debajo del campo puede ser de 100px, y las estaciones que no caben quedan detrás del
    // teclado sin manera de llegar a ellas. `visualViewport` es lo único que sabe dónde acaba
    // lo que el cliente ve --`innerHeight` no se entera de que hay un teclado-- y donde no
    // exista se cae a la ventana entera, que es como funcionaba hasta ahora.
    const visual = window.visualViewport
    const fondoVisible = visual ? visual.offsetTop + visual.height : window.innerHeight
    const hueco = fondoVisible - r.bottom - MARGEN_LISTA
    // Nunca por debajo del mínimo: una lista de 20px no sirve para nada, y es preferible que
    // asome por debajo a que desaparezca. Con el scroll propio de la lista se llega al resto.
    lista.style.maxHeight = Math.max(ALTO_MINIMO_LISTA, Math.min(ALTO_MAXIMO_LISTA, hueco)) + 'px'
  }

  function abrir () {
    if (abierta) return
    abierta = true
    lista.classList.remove('rvb-oculto')
    campo.setAttribute('aria-expanded', 'true')
    colocar()
    window.addEventListener('scroll', colocar, true)
    window.addEventListener('resize', colocar)
    // El teclado del móvil NO dispara el `resize` de la ventana --en iOS `innerHeight` no se
    // mueve cuando sale--, pero sí el del viewport visual: sin esto, la lista se coloca una vez
    // con la pantalla entera y se queda con un alto que ya no cabe.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', colocar)
      window.visualViewport.addEventListener('scroll', colocar)
    }
  }

  function cerrar () {
    if (!abierta) return
    abierta = false
    lista.classList.add('rvb-oculto')
    campo.setAttribute('aria-expanded', 'false')
    campo.removeAttribute('aria-activedescendant')
    marcado = -1
    window.removeEventListener('scroll', colocar, true)
    window.removeEventListener('resize', colocar)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', colocar)
      window.visualViewport.removeEventListener('scroll', colocar)
    }
  }

  function escapar (t) {
    return String(t == null ? '' : t)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }

  /** Resalta en la sugerencia el trozo que el cliente ha escrito, como hacía `highlight`. */
  function resaltar (texto, escrito) {
    const limpio = escapar(texto)
    const q = normalizar(escrito)
    if (!q) return limpio
    const donde = normalizar(texto).indexOf(q)
    if (donde === -1) return limpio
    // Se corta sobre el texto ORIGINAL usando las posiciones del normalizado: los dos tienen
    // la misma longitud porque normalizar sólo quita marcas de acento, no letras.
    return escapar(texto.slice(0, donde))
      + '<strong>' + escapar(texto.slice(donde, donde + escrito.trim().length)) + '</strong>'
      + escapar(texto.slice(donde + escrito.trim().length))
  }

  function render (escrito) {
    lista.innerHTML = ''
    if (!resultados.length) { cerrar(); return }
    resultados.forEach((item, i) => {
      const fila = document.createElement('li')
      fila.className = 'rvb-tt-suggestion rvb-tt-selectable'
      fila.id = id + '-' + i
      fila.setAttribute('role', 'option')
      fila.setAttribute('aria-selected', 'false')
      fila.innerHTML = pintar ? pintar(item, resaltar, escrito) : resaltar(etiqueta(item), escrito)
      // mousedown y no click: el click llega después del blur del campo, y para entonces el
      // blur ya habría cerrado la lista.
      fila.addEventListener('mousedown', (e) => { e.preventDefault(); elegir(i) })
      lista.appendChild(fila)
    })
    abrir()
    // Y se recoloca aunque ya estuviera abierta: `abrir()` no hace nada la segunda vez, y
    // entre una tanda de sugerencias y la siguiente el campo ha podido moverse --en el móvil,
    // sin ir más lejos, la primera letra abre el panel a pantalla completa y el campo se va
    // de media pantalla a la primera línea--.
    colocar()
  }

  function marcar (i) {
    const filas = [...lista.children]
    filas.forEach((f) => { f.classList.remove('rvb-tt-cursor'); f.setAttribute('aria-selected', 'false') })
    if (i < 0 || i >= filas.length) { campo.removeAttribute('aria-activedescendant'); marcado = -1; return }
    marcado = i
    filas[i].classList.add('rvb-tt-cursor')
    filas[i].setAttribute('aria-selected', 'true')
    campo.setAttribute('aria-activedescendant', filas[i].id)
    // que la fila marcada se vea aunque la lista tenga scroll
    const f = filas[i]
    if (f.offsetTop < lista.scrollTop) lista.scrollTop = f.offsetTop
    else if (f.offsetTop + f.offsetHeight > lista.scrollTop + lista.clientHeight) {
      lista.scrollTop = f.offsetTop + f.offsetHeight - lista.clientHeight
    }
  }

  function elegir (i) {
    const item = resultados[i]
    if (!item) return
    campo.value = etiqueta(item)
    cerrar()
    alElegir(item)
    campo.blur()
  }

  /**
   * Enseña lo que ya se sabe, sin pedir nada. Es un ADELANTO: la respuesta del servidor llega
   * detrás y manda, porque la caché no tiene por qué estar completa (el endpoint devuelve como
   * mucho 20 filas, así que nadie puede tener el catálogo entero sólo a base de consultas).
   */
  function adelantar (escrito) {
    if (!adelanto) return false
    let filas = null
    try { filas = adelanto(escrito) } catch (e) { filas = null }
    if (!filas || !filas.length) return false
    resultados = filas
    render(escrito)
    return true
  }

  async function pedir (escrito) {
    if (peticion) peticion.abort()
    peticion = new AbortController()
    try {
      const filas = await buscar(escrito, peticion.signal)
      resultados = Array.isArray(filas) ? filas : []
      if (alResponder) { try { alResponder(escrito, resultados) } catch (e) { /* la caché no manda */ } }
      render(escrito)
    } catch (e) {
      // Una petición cancelada por otra más nueva no es un fallo. Cualquier otro fallo se
      // traga: el buscador tiene que seguir usable aunque el autocompletado no conteste.
      if (e && e.name !== 'AbortError') console.warn('[buscador] autocompletado: ' + e.message)
    }
  }

  function alTeclear () {
    const escrito = campo.value
    // Teclear invalida lo que hubiera elegido antes: en la home cada keydown borra el
    // typeaheadValue, y de ahí depende que no se pueda buscar con una estación a medio
    // escribir que el servidor nunca devolvió.
    alInvalidar()
    clearTimeout(temporizador)
    if (escrito.trim().length < minimo) { resultados = []; cerrar(); return }
    // Lo que ya se sepa se enseña AHORA, sin esperar ni al antirrebote ni al servidor. La
    // petición se lanza igual y su respuesta sustituye a esto.
    adelantar(escrito)
    temporizador = setTimeout(() => pedir(escrito), ESPERA_MS)
  }

  campo.addEventListener('input', alTeclear)

  campo.addEventListener('focus', () => {
    // Con mínimo 0, al enfocar ya se ofrece la lista, como hace hoy typeahead en las
    // estaciones. Con mínimo 1 (ocio) no se ofrece nada hasta que se escribe.
    if (campo.value.trim().length < minimo) return
    clearTimeout(temporizador)
    adelantar(campo.value)
    temporizador = setTimeout(() => pedir(campo.value), ESPERA_MS)
  })

  campo.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!abierta) { if (campo.value.trim().length >= minimo) pedir(campo.value) }
      else marcar(Math.min(marcado + 1, resultados.length - 1))
    }
    else if (e.key === 'ArrowUp') { e.preventDefault(); marcar(Math.max(marcado - 1, 0)) }
    else if (e.key === 'Enter') {
      // Enter con una fila marcada la elige; sin lista abierta, deja que el formulario siga.
      if (abierta && marcado >= 0) { e.preventDefault(); elegir(marcado) }
    } else if (e.key === 'Escape') { cerrar() }
    else if (e.key === 'Tab') { if (abierta && marcado >= 0) elegir(marcado); else cerrar() }
  })

  campo.addEventListener('blur', () => {
    // Un poco de margen para que un mousedown sobre la lista llegue antes de cerrarla.
    setTimeout(cerrar, 120)
  })

  return {
    cerrar,
    /** Fija el campo a un item ya conocido, sin pedir nada ni disparar la elección. */
    fijar (item) { campo.value = etiqueta(item) },
    destruir () {
      clearTimeout(temporizador)
      if (peticion) peticion.abort()
      cerrar()
      lista.remove()
    },
  }
}
